import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import { Button } from "../../../shared";
import { buildAffiliateProfilePath } from "../../../../utils/NavigationUtils";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type {
  CreateAffiliateResponseData,
  PromoCodeLinkStatus,
} from "../../../../types/affiliates";

import styles from "./Confirmation.module.css";

const STATUS_COPY: Record<PromoCodeLinkStatus, { label: string }> = {
  pending: { label: AppStrings.NEW_AFFILIATE_CONFIRMATION_STATUS_PENDING },
  provisioned: {
    label: AppStrings.NEW_AFFILIATE_CONFIRMATION_STATUS_PROVISIONED,
  },
  failed: { label: AppStrings.NEW_AFFILIATE_CONFIRMATION_STATUS_FAILED },
  deleted: { label: AppStrings.APPLINK_DELETED },
  not_applicable: { label: AppStrings.NOT_AVAILABLE },
  unknown: { label: AppStrings.UNKNOWN },
};

const renderStatus = (status: PromoCodeLinkStatus | string) => {
  switch (status) {
    case "pending":
      return (
        <span className={styles.statusPending}>
          <ClockCircleOutlined /> {STATUS_COPY.pending.label}
        </span>
      );
    case "provisioned":
      return (
        <span className={styles.statusProvisioned}>
          <CheckCircleOutlined /> {STATUS_COPY.provisioned.label}
        </span>
      );
    case "failed":
      return (
        <span className={styles.statusFailed}>
          <WarningOutlined /> {STATUS_COPY.failed.label}
        </span>
      );
    case "deleted":
      return (
        <span className={styles.statusUnknown}>
          <DeleteOutlined /> {STATUS_COPY.deleted.label}
        </span>
      );
    case "not_applicable":
      return (
        <span className={styles.statusUnknown}>
          <MinusCircleOutlined /> {STATUS_COPY.not_applicable.label}
        </span>
      );
    case "unknown":
      return (
        <span className={styles.statusUnknown}>
          <QuestionCircleOutlined /> {STATUS_COPY.unknown.label}
        </span>
      );
    default:
      return <span className={styles.statusUnknown}>{status}</span>;
  }
};

export type ConfirmationProps = {
  response: CreateAffiliateResponseData;
  recipientEmail?: string;
};

const Confirmation: FC<ConfirmationProps> = ({ response, recipientEmail }) => {
  const navigate = useNavigate();

  const handleCopy = useCallback(
    async (text: string, successMessage: string) => {
      try {
        await navigator.clipboard.writeText(text);
        message.success(successMessage);
      } catch {
        message.error(AppStrings.NEW_AFFILIATE_CONFIRMATION_COPY_FAILED);
      }
    },
    [],
  );

  const handleGoToProfile = useCallback(() => {
    navigate(buildAffiliateProfilePath(response.affiliate_id));
  }, [navigate, response.affiliate_id]);

  const entries = Object.entries(response.promo_code_link_statuses ?? {});
  const usernameDisplay = response.cognito_username ?? "—";
  const inviteEmail =
    recipientEmail?.trim() || response.cognito_username || "—";

  return (
    <section
      className={styles.container}
      data-testid="new-affiliate-confirmation"
    >
      <h2 className={styles.title}>
        {AppStrings.NEW_AFFILIATE_CONFIRMATION_TITLE}
      </h2>

      <div className={styles.summary}>
        <span className={styles.label}>
          {AppStrings.NEW_AFFILIATE_CONFIRMATION_AFFILIATE_ID}
        </span>
        <span className={styles.value}>
          <code>{response.affiliate_id}</code>
          <button
            type="button"
            className={styles.copyButton}
            onClick={() =>
              handleCopy(
                response.affiliate_id,
                AppStrings.NEW_AFFILIATE_CONFIRMATION_AFFILIATE_ID_COPIED,
              )
            }
            aria-label={
              AppStrings.NEW_AFFILIATE_CONFIRMATION_COPY_AFFILIATE_ID_ARIA
            }
            data-testid="confirmation-copy-affiliate-id"
          >
            <CopyOutlined /> {AppStrings.NEW_AFFILIATE_CONFIRMATION_COPY}
          </button>
        </span>

        <span className={styles.label}>
          {AppStrings.NEW_AFFILIATE_CONFIRMATION_SIGN_IN_USERNAME}
        </span>
        <span className={styles.value}>
          <code>{usernameDisplay}</code>
          <button
            type="button"
            className={styles.copyButton}
            disabled={!response.cognito_username}
            onClick={() =>
              handleCopy(
                response.cognito_username ?? "",
                AppStrings.NEW_AFFILIATE_CONFIRMATION_USERNAME_COPIED,
              )
            }
            aria-label={
              AppStrings.NEW_AFFILIATE_CONFIRMATION_COPY_USERNAME_ARIA
            }
            data-testid="confirmation-copy-username"
          >
            <CopyOutlined /> {AppStrings.NEW_AFFILIATE_CONFIRMATION_COPY}
          </button>
        </span>
      </div>

      <p
        className={styles.inviteNote}
        data-testid="confirmation-invite-note"
      >
        {AppStrings.NEW_AFFILIATE_CONFIRMATION_EMAIL_SENT.replace(
          "{email}",
          inviteEmail,
        )}
      </p>

      {entries.length === 0 ? (
        <div
          className={styles.emptyCodes}
          data-testid="confirmation-no-promo-codes"
        >
          {AppStrings.NEW_AFFILIATE_CONFIRMATION_NO_PROMO_CODES}
        </div>
      ) : (
        <table
          className={styles.table}
          data-testid="confirmation-promo-codes-table"
        >
          <thead>
            <tr>
              <th scope="col">
                {AppStrings.NEW_AFFILIATE_CONFIRMATION_PROMO_CODE_COLUMN}
              </th>
              <th scope="col">
                {AppStrings.NEW_AFFILIATE_CONFIRMATION_APPLINK_STATUS_COLUMN}
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([code, status]) => (
              <tr
                key={code}
                data-testid={`confirmation-promo-code-row-${code}`}
              >
                <td>
                  <code>{code}</code>
                </td>
                <td data-testid={`confirmation-promo-code-status-${code}`}>
                  {renderStatus(status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className={styles.footer}>
        <Button
          intent="primary"
          onClick={handleGoToProfile}
          data-testid="confirmation-go-to-profile"
        >
          {AppStrings.NEW_AFFILIATE_CONFIRMATION_GO_TO_PROFILE}
        </Button>
      </div>
    </section>
  );
};

export default Confirmation;
